'use client';

import React, { useState, useCallback, useEffect, useId, useRef } from 'react';
import { useModalFocusManagement } from '@/hooks/useModalFocusManagement';

// ============================================================================
// Strings (extracted for future i18n — see docs/I18N_CONVENTIONS.md)
// ============================================================================
const STRINGS = {
	title: "Two-Factor Authentication",
	totpDescription: "Enter the 6-digit code from your authenticator app.",
	emailDescription: "Enter the 6-digit code sent to your email.",
	smsDescription: "Enter the 6-digit code sent to your phone.",
	smsSending: "Sending verification code...",
	smsSentTo: (phone: string) => `Code sent to ${phone}`,
	emailSending: "Sending verification code...",
	codePlaceholder: "000000",
	verifyButton: "Verify",
	verifying: "Verifying...",
	cancelButton: "Cancel",
	backupToggle: "Use a backup code instead",
	backupLabel: "Backup Code:",
	backupPlaceholder: "XXXX-XXXX",
	backupHint: "Enter one of the backup codes you saved when setting up 2FA.",
	backToCode: "Use verification code instead",
	resendButton: "Resend Code",
	resendCountdown: (seconds: number) => `Resend in ${seconds}s`,
	codeExpiry: "Code expires in 10 minutes.",
	emailSentTo: (email: string) => `Code sent to ${email}`,
	genericError: "Verification failed. Please try again.",
	failedToSendCode: "Failed to send code",
	failedToSendVerificationCode: "Failed to send verification code",
	codeRequired: "Please enter a code",
	invalidSixDigitCode: "Please enter a valid 6-digit code",
	codeInputLabel: "Six-digit verification code",
} as const;

interface LoginTwoFactorModalProps {
	isOpen: boolean;
	method: 'totp' | 'email' | 'sms' | string;
	userEmail: string;
	userPhone?: string;
	onVerified: () => void;
	onCancel: () => void;
	returnFocusElement: HTMLElement | null;
	getFallbackFocusElement?: () => HTMLElement | null;
}

/**
 * Login-time 2FA verification modal.
 * Shown after successful password authentication for users with 2FA enabled.
 * Supports both TOTP (authenticator app) and Email OTP methods.
 */
export function LoginTwoFactorModal({
	isOpen,
	method,
	userEmail,
	userPhone,
	onVerified,
	onCancel,
	returnFocusElement,
	getFallbackFocusElement,
}: LoginTwoFactorModalProps) {
	const [code, setCode] = useState('');
	const [backupCode, setBackupCode] = useState('');
	const [error, setError] = useState('');
	const [isVerifying, setIsVerifying] = useState(false);
	const [useBackup, setUseBackup] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const [isSendingEmail, setIsSendingEmail] = useState(false);
	const [resendCooldown, setResendCooldown] = useState(0);
	const codeInputRef = useRef<HTMLInputElement>(null);
	const backupInputRef = useRef<HTMLInputElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
	const titleId = useId();
	const descriptionId = useId();
	const backupInputId = useId();
	const isEmailMethod = method === 'email';
	const isSmsMethod = method === 'sms';
	const isCodeMethod = isEmailMethod || isSmsMethod;

	useModalFocusManagement({
		dialogRef,
		enabled: isOpen,
		getFallbackFocusElement,
		initialFocusRef: codeInputRef,
		returnFocusElement,
	});

	// Mask email for display (j***@example.com)
	const maskedEmail = userEmail
		? userEmail.replace(/^(.{1,2})(.*)(@.*)$/, (_, start, middle, domain) =>
			start + '*'.repeat(Math.min(middle.length, 5)) + domain
		  )
		: '';

	// Mask phone for display (***-***-4567)
	const maskedPhone = userPhone
		? userPhone.length >= 4
			? '***-***-' + userPhone.slice(-4)
			: '***'
		: '';

	// Auto-send OTP when modal opens for email or SMS method
	useEffect(() => {
		if (isOpen && (method === 'email' || method === 'sms') && !emailSent) {
			sendEmailCode();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, method]);

	// Focus the active verification input when the modal opens or switches mode.
	useEffect(() => {
		if (!isOpen) return undefined;

		const timer = window.setTimeout(() => {
			const input = useBackup ? backupInputRef.current : codeInputRef.current;
			if (!input?.disabled) input?.focus({ preventScroll: true });
		}, 100);

		return () => window.clearTimeout(timer);
	}, [isOpen, useBackup, emailSent]);

	// Cleanup cooldown timer
	useEffect(() => {
		return () => {
			if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		};
	}, []);

	// Reset state when modal closes
	useEffect(() => {
		if (!isOpen) {
			setCode('');
			setBackupCode('');
			setError('');
			setIsVerifying(false);
			setUseBackup(false);
			setEmailSent(false);
			setIsSendingEmail(false);
			setResendCooldown(0);
			if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		}
	}, [isOpen]);

	const startResendCooldown = useCallback(() => {
		setResendCooldown(30);
		if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
		cooldownTimerRef.current = setInterval(() => {
			setResendCooldown((prev) => {
				if (prev <= 1) {
					if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, []);

	const sendEmailCode = useCallback(async () => {
		setIsSendingEmail(true);
		setError('');
		try {
			const res = await fetch('/api/auth/2fa/send-code', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
			});
			const data = await res.json();
			if (!res.ok) {
				setError(data.error || STRINGS.failedToSendCode);
				return;
			}
			setEmailSent(true);
			startResendCooldown();
		} catch {
			setError(STRINGS.failedToSendVerificationCode);
		} finally {
			setIsSendingEmail(false);
		}
	}, [startResendCooldown]);

	const handleVerify = useCallback(async () => {
		const verifyCode = useBackup ? undefined : code.replace(/\s/g, '').trim();
		const verifyBackup = useBackup ? backupCode.trim() : undefined;

		if (!verifyCode && !verifyBackup) {
			setError(STRINGS.codeRequired);
			return;
		}

		// Validate 6-digit code
		if (verifyCode && !/^\d{6}$/.test(verifyCode)) {
			setError(STRINGS.invalidSixDigitCode);
			return;
		}

		setIsVerifying(true);
		setError('');

		try {
			const res = await fetch('/api/auth/2fa/verify', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'include',
				body: JSON.stringify({
					code: verifyCode,
					backupCode: verifyBackup,
				}),
			});
			const data = await res.json();

			if (!res.ok) {
				setError(data.error || STRINGS.genericError);
				setIsVerifying(false);
				return;
			}

			// Success — let the parent handle session.update + redirect
			onVerified();
		} catch {
			setError(STRINGS.genericError);
			setIsVerifying(false);
		}
	}, [code, backupCode, useBackup, onVerified]);

	// Handle Enter key
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !isVerifying) {
				handleVerify();
			}
		},
		[handleVerify, isVerifying]
	);

	const handleDialogKeyDown = useCallback((event: React.KeyboardEvent) => {
		if (event.key !== 'Escape') return;

		event.preventDefault();
		event.stopPropagation();
		if (!isVerifying) onCancel();
	}, [isVerifying, onCancel]);

	// Handle code input — auto-submit on 6 digits
	const handleCodeChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value.replace(/\D/g, '').slice(0, 6);
			setCode(val);
			setError('');
		},
		[]
	);

	if (!isOpen) return null;

	return (
		<div
			data-testid="login-two-factor-overlay"
			onKeyDown={handleDialogKeyDown}
			style={{
				position: 'absolute',
				inset: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 100,
				borderRadius: 40,
				overflow: 'hidden',
			}}
		>
			{/* Backdrop */}
			<div
				aria-hidden="true"
				style={{
					position: 'absolute',
					inset: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			{/* Modal */}
			<div
				ref={dialogRef}
				aria-busy={isVerifying || isSendingEmail}
				aria-describedby={descriptionId}
				aria-labelledby={titleId}
				aria-modal="true"
				data-testid="login-two-factor-dialog"
				role="dialog"
				tabIndex={-1}
				style={{
					position: 'relative',
					width: 340,
					backgroundColor: '#fff',
					borderRadius: 20,
					padding: '32px 24px',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
					display: 'flex',
					flexDirection: 'column',
					gap: 20,
				}}
			>
				{/* Header */}
				<div style={{ textAlign: 'center' }}>
					{/* Lock icon */}
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: '50%',
							backgroundColor: '#EBF5FF',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							margin: '0 auto 12px',
						}}
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							stroke="#5BA9E1"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
							<path d="M7 11V7a5 5 0 0 1 10 0v4" />
						</svg>
					</div>
					<h2
						id={titleId}
						style={{
							fontSize: 18,
							fontWeight: 600,
							fontFamily: 'Inter, sans-serif',
							color: '#1a1a1a',
							margin: 0,
						}}
					>
						{STRINGS.title}
					</h2>
					<p
						id={descriptionId}
						style={{
							fontSize: 13,
							fontFamily: 'Inter, sans-serif',
							color: '#666',
							margin: '8px 0 0',
							lineHeight: 1.4,
						}}
					>
						{useBackup
							? STRINGS.backupHint
						: isSmsMethod
							? isSendingEmail
								? STRINGS.smsSending
								: STRINGS.smsDescription
							: isEmailMethod
								? isSendingEmail
									? STRINGS.emailSending
									: STRINGS.emailDescription
								: STRINGS.totpDescription}
					</p>
					{isEmailMethod && emailSent && !useBackup && (
						<p
							style={{
								fontSize: 12,
								fontFamily: 'Inter, sans-serif',
								color: '#5BA9E1',
								margin: '4px 0 0',
							}}
						>
							{STRINGS.emailSentTo(maskedEmail)}
						</p>
					)}
					{isSmsMethod && emailSent && !useBackup && (
						<p
							style={{
								fontSize: 12,
								fontFamily: 'Inter, sans-serif',
								color: '#5BA9E1',
								margin: '4px 0 0',
							}}
						>
							{STRINGS.smsSentTo(maskedPhone)}
						</p>
					)}
				</div>

				{/* Code Input */}
				{!useBackup ? (
					<div>
						<input
							ref={codeInputRef}
							aria-label={STRINGS.codeInputLabel}
							data-testid="login-two-factor-code-input"
							type="text"
							inputMode="numeric"
							autoComplete="one-time-code"
							value={code}
							onChange={handleCodeChange}
							onKeyDown={handleKeyDown}
							placeholder={STRINGS.codePlaceholder}
							disabled={isVerifying || (isCodeMethod && !emailSent)}
							style={{
								width: '100%',
								padding: '14px 16px',
								fontSize: 24,
								fontFamily: "'SF Mono', 'Fira Code', monospace",
								fontWeight: 500,
								textAlign: 'center',
								letterSpacing: 8,
								border: `2px solid ${error ? '#E53E3E' : '#e2e2e2'}`,
								borderRadius: 12,
								outline: 'none',
								backgroundColor: isVerifying || (isCodeMethod && !emailSent) ? '#f5f5f5' : '#fff',
								color: '#1a1a1a',
								transition: 'border-color 0.2s',
								boxSizing: 'border-box',
							}}
							onFocus={(e) => {
								if (!error) e.currentTarget.style.borderColor = '#5BA9E1';
							}}
							onBlur={(e) => {
								if (!error) e.currentTarget.style.borderColor = '#e2e2e2';
							}}
						/>
						{isCodeMethod && (
							<p
								style={{
									fontSize: 11,
									fontFamily: 'Inter, sans-serif',
									color: '#999',
									margin: '6px 0 0',
									textAlign: 'center',
								}}
							>
								{STRINGS.codeExpiry}
							</p>
						)}
					</div>
				) : (
					<div>
						<label
							htmlFor={backupInputId}
							style={{
								display: 'block',
								fontSize: 13,
								fontFamily: 'Inter, sans-serif',
								fontWeight: 500,
								color: '#333',
								marginBottom: 6,
							}}
						>
							{STRINGS.backupLabel}
						</label>
						<input
							ref={backupInputRef}
							id={backupInputId}
							data-testid="login-two-factor-backup-input"
							type="text"
							value={backupCode}
							onChange={(e) => {
								setBackupCode(e.target.value);
								setError('');
							}}
							onKeyDown={handleKeyDown}
							placeholder={STRINGS.backupPlaceholder}
							disabled={isVerifying}
							style={{
								width: '100%',
								padding: '12px 16px',
								fontSize: 16,
								fontFamily: "'SF Mono', 'Fira Code', monospace",
								fontWeight: 500,
								textAlign: 'center',
								letterSpacing: 2,
								textTransform: 'uppercase',
								border: `2px solid ${error ? '#E53E3E' : '#e2e2e2'}`,
								borderRadius: 12,
								outline: 'none',
								backgroundColor: isVerifying ? '#f5f5f5' : '#fff',
								color: '#1a1a1a',
								boxSizing: 'border-box',
							}}
							onFocus={(e) => {
								if (!error) e.currentTarget.style.borderColor = '#5BA9E1';
							}}
							onBlur={(e) => {
								if (!error) e.currentTarget.style.borderColor = '#e2e2e2';
							}}
						/>
					</div>
				)}

				{/* Error */}
				{error && (
					<p
						role="alert"
						style={{
							fontSize: 13,
							fontFamily: 'Inter, sans-serif',
							color: '#E53E3E',
							textAlign: 'center',
							margin: 0,
						}}
					>
						{error}
					</p>
				)}

				{/* Buttons */}
				<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
					<button
						type="button"
						data-testid="login-two-factor-verify"
						onClick={handleVerify}
						disabled={isVerifying || (isCodeMethod && !emailSent && !useBackup)}
						style={{
							padding: '12px 20px',
							fontSize: 15,
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							color: '#fff',
							backgroundColor:
								isVerifying || (isCodeMethod && !emailSent && !useBackup)
									? '#a0c4e8'
									: '#5BA9E1',
							border: 'none',
							borderRadius: 12,
							cursor:
								isVerifying || (isCodeMethod && !emailSent && !useBackup)
									? 'not-allowed'
									: 'pointer',
							transition: 'background-color 0.2s',
						}}
					>
						{isVerifying ? STRINGS.verifying : STRINGS.verifyButton}
					</button>

					<button
						type="button"
						data-testid="login-two-factor-cancel"
						onClick={onCancel}
						disabled={isVerifying}
						style={{
							padding: '10px 20px',
							fontSize: 14,
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							color: '#666',
							backgroundColor: 'transparent',
							border: '1px solid #e2e2e2',
							borderRadius: 12,
							cursor: isVerifying ? 'not-allowed' : 'pointer',
						}}
					>
						{STRINGS.cancelButton}
					</button>
				</div>

				{/* Toggle: backup code / verification code */}
				<div style={{ textAlign: 'center' }}>
					<button
						type="button"
						onClick={() => {
							setUseBackup(!useBackup);
							setError('');
							setCode('');
							setBackupCode('');
						}}
						style={{
							fontSize: 13,
							fontFamily: 'Inter, sans-serif',
							color: '#5BA9E1',
							backgroundColor: 'transparent',
							border: 'none',
							cursor: 'pointer',
							textDecoration: 'underline',
							padding: 0,
						}}
					>
						{useBackup ? STRINGS.backToCode : STRINGS.backupToggle}
					</button>
				</div>

				{/* Resend button for email/SMS method */}
				{isCodeMethod && !useBackup && emailSent && (
					<div style={{ textAlign: 'center' }}>
						<button
							type="button"
							onClick={sendEmailCode}
							disabled={resendCooldown > 0 || isSendingEmail}
							style={{
								fontSize: 13,
								fontFamily: 'Inter, sans-serif',
								color:
									resendCooldown > 0 || isSendingEmail ? '#999' : '#5BA9E1',
								backgroundColor: 'transparent',
								border: 'none',
								cursor:
									resendCooldown > 0 || isSendingEmail
										? 'not-allowed'
										: 'pointer',
								textDecoration: 'underline',
								padding: 0,
							}}
						>
							{resendCooldown > 0
								? STRINGS.resendCountdown(resendCooldown)
								: isSendingEmail
									? STRINGS.emailSending
									: STRINGS.resendButton}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
